import { Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'mantine-react-table';
import { describeClinicalTaskDue } from '@bariendo/lib/clinical-tasks';
import { LastCommunicationCell, NextTaskCell } from '../services/listRowShared';
import { PersonCell } from './PersonCell';

/**
 * Column definitions every PAC queue renders the same way.
 *
 * One definition each, so a change to how a date or a task reads cannot land on
 * one tab and miss the others. Every accessor tolerates a missing row — see the
 * header of `preConsultationColumns.tsx` for why that is a hard requirement.
 */

const DATE_FORMAT = 'MMM D, YYYY';

/** Dates carry the year: these queues are read a year later during win-back. */
export function formatListDate(value?: string): string {
  return value ? dayjs(value).format(DATE_FORMAT) : '';
}

/** The row shape the shared columns read. */
interface QueueRowLike {
  lastCommunication?: LastCommunicationCell;
  nextTask?: NextTaskCell;
}

export function lastCommunicationColumn<T extends QueueRowLike>(): MRT_ColumnDef<T> {
  return {
    id: 'lastCommunication',
    header: 'Last communication',
    accessorFn: (row) => row?.lastCommunication?.sent ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => (
      <PersonCell
        text={formatListDate(row.original.lastCommunication?.sent)}
        name={row.original.lastCommunication?.name}
        systemLabel={row.original.lastCommunication?.systemLabel}
      />
    ),
  };
}

/**
 * Task dates deliberately carry no year: they are all within a few weeks of
 * today, this is the narrowest column, and a year on every row buys nothing
 * while pushing the task title into an ellipsis sooner.
 * `describeClinicalTaskDue` already implements that rule.
 *
 * The avatar is the assignee, which is what makes the column safe on a shared
 * queue. It is absent when the task sits in a role pool rather than with a
 * person — the row still answers "what am I late on".
 */
export function nextTaskColumn<T extends QueueRowLike>(now: Date): MRT_ColumnDef<T> {
  return {
    id: 'nextTask',
    header: 'Next task',
    accessorFn: (row) => row?.nextTask?.dueDate ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const task = row.original.nextTask;

      if (!task) {
        return '—';
      }

      const due = describeClinicalTaskDue(task.dueDate, now);

      return (
        <Tooltip label={task.title ?? due.label} withArrow position="top-start">
          <span>
            <PersonCell text={due.label} name={task.ownerName} emphasis={due.overdue ? 'warning' : undefined} />
          </span>
        </Tooltip>
      );
    },
  };
}
